/**
 * 输入管理：键盘 + 虚拟通道（触屏摇杆/按钮）。
 * 对外只暴露「状态查询」，不直接操作实体。
 * 虚拟通道由 ui/touch.js 写入，二者对玩法层完全等价。
 */
/** 焦点是不是落在能打字的地方（输入框、文本域、下拉、可编辑块） */
function isTypingTarget(el) {
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!el.isContentEditable;
}

export class Input {
  constructor(target = window) {
    this.down = new Set();
    this.pressed = new Set();   // 本帧刚按下
    this.released = new Set();  // 本帧刚松开
    this.target = target;
    this.touchAxis = null;      // {x, y} 长度 <= 1，由摇杆写入

    this._onKeyDown = (e) => {
      // 先按事件自带的修饰键状态校正：输入法、面板、iframe 常把 keyup 吞掉，
      // 光看 down 集合就会把「按住」记成永久按下（Shift 卡住 = 一直跑步）
      this._syncMods(e);
      if (e.repeat) return;
      // 在输入框里写字时，字母数字空格只当字，不当操作（免得一打字人就走了、一空格人就跳了）
      if (isTypingTarget(e.target) && e.key && e.key.length === 1) return;
      const k = this._norm(e.key);
      if (!this.down.has(k)) this.pressed.add(k);
      this.down.add(k);
      // 阻止方向键/空格滚动页面
      if ([' ', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) e.preventDefault();
    };
    this._onKeyUp = (e) => {
      const k = this._norm(e.key);
      this.down.delete(k);
      this.released.add(k);
      this._syncMods(e);
    };
    this._onBlur = () => this.clear();

    target.addEventListener('keydown', this._onKeyDown);
    target.addEventListener('keyup', this._onKeyUp);
    target.addEventListener('blur', this._onBlur);
  }

  _norm(key) {
    return key.length === 1 ? key.toLowerCase() : key;
  }

  /**
   * 用事件自带的修饰键状态校正 Shift / Ctrl / Alt / Meta。
   * 任何一个键盘事件都带着这几个键的真实状态，所以哪怕某一次 keyup 丢了，
   * 下次随便按个什么键（或等它自己松开）就能把卡住的修饰键纠正回来。
   */
  _syncMods(e) {
    if (!e || typeof e.shiftKey !== 'boolean') return;
    const mods = [['Shift', e.shiftKey], ['Control', e.ctrlKey], ['Alt', e.altKey], ['Meta', e.metaKey]];
    for (const [k, on] of mods) {
      if (typeof on !== 'boolean') continue;
      if (on) { if (!this.down.has(k)) { this.down.add(k); this.pressed.add(k); } }
      else if (this.down.has(k)) { this.down.delete(k); this.released.add(k); }
    }
  }

  /** 全松开：键盘、摇杆、瞬时状态一并清空（开面板、窗口失焦时用） */
  clear() {
    this.down.clear();
    this.pressed.clear();
    this.released.clear();
    this.touchAxis = null;
    if (this._pendingRelease) this._pendingRelease.length = 0;
  }

  isDown(key) { return this.down.has(this._norm(key)); }
  justPressed(key) { return this.pressed.has(this._norm(key)); }
  justReleased(key) { return this.released.has(this._norm(key)); }

  /** 归一化的移动向量；摇杆优先，键盘兜底 */
  axis() {
    const t = this.touchAxis;
    if (t && (t.x !== 0 || t.y !== 0)) return { x: t.x, y: t.y };

    let x = 0, y = 0;
    if (this.isDown('a') || this.isDown('ArrowLeft')) x -= 1;
    if (this.isDown('d') || this.isDown('ArrowRight')) x += 1;
    if (this.isDown('w') || this.isDown('ArrowUp')) y -= 1;
    if (this.isDown('s') || this.isDown('ArrowDown')) y += 1;
    if (x !== 0 && y !== 0) {
      const inv = Math.SQRT1_2;
      x *= inv; y *= inv;
    }
    return { x, y };
  }

  /* ---------- 虚拟通道（触屏） ---------- */

  /** 摇杆写入方向向量，传 null 表示松开 */
  setAxis(x, y) {
    if (x === null || (x === 0 && y === 0)) { this.touchAxis = null; return; }
    const len = Math.hypot(x, y) || 1;
    const k = Math.min(1, len) / len;
    this.touchAxis = { x: x * k, y: y * k };
  }

  /** 虚拟按键按下（与真实键盘同语义） */
  virtualDown(key) {
    const k = this._norm(key);
    if (!this.down.has(k)) this.pressed.add(k);
    this.down.add(k);
  }

  /** 虚拟按键松开 */
  virtualUp(key) {
    const k = this._norm(key);
    this.down.delete(k);
    this.released.add(k);
  }

  /** 点按一下：本帧按下，下一帧自动松开 */
  virtualTap(key) {
    this.virtualDown(key);
    this._pendingRelease = this._pendingRelease || [];
    this._pendingRelease.push(this._norm(key));
  }

  /** 每帧末尾调用，清理瞬时状态 */
  endFrame() {
    this.pressed.clear();
    this.released.clear();
    // 延后到清理之后再释放，保证 justPressed / justReleased 各生效一帧
    if (this._pendingRelease && this._pendingRelease.length) {
      for (const k of this._pendingRelease) this.virtualUp(k);
      this._pendingRelease.length = 0;
    }
  }

  destroy() {
    this.target.removeEventListener('keydown', this._onKeyDown);
    this.target.removeEventListener('keyup', this._onKeyUp);
    this.target.removeEventListener('blur', this._onBlur);
  }
}
